import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';

export type Types = 'string' | 'number' | 'date' | 'dateTime' | 'time' | 'boolean' | 'percentage';

export interface ColumnOption {
  id?: string;
  title?: string;
  tdClass?: string;
  thClass?: string;
  display?: boolean;
  type?: Types;
  /** Include in Excel/CSV export even when `display` is false. */
  exportAlways?: boolean;
  /** Include in PDF export. Defaults to true; set false to keep a column out of the PDF. */
  pdf?: boolean;
  /** Groups columns under a shared toggle in the Table Options panel (e.g. a transaction type). */
  group?: string;
  /** Export-only column: never surfaced in the Table Options panel or on screen. */
  exportOnly?: boolean;
}

export type columnsOptions = { [colName: string]: ColumnOption };

@Component({
  selector: 'app-data-table',
  templateUrl: './data-table.component.html',
  styleUrls: ['./data-table.component.scss'],
})
export class DataTableComponent implements OnInit, OnChanges {
  constructor() {}

  @Input() columnsOptions: columnsOptions = {};
  @Input() displayedColumns: string[] = [];
  @Input() dataSource: any[] = [];
  @Input() loading: boolean = true;

  ngOnInit(): void {
    if (this.displayedColumns.length === 0 && this.dataSource.length !== 0) {
      this.changeDisplayedCols();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    for (const propName in changes) {
      if (changes.hasOwnProperty(propName) && !changes[propName].firstChange) {
        switch (propName) {
          case 'displayedColumns': {
            //
            break;
          }
          case 'dataSource': {
            this.changeDisplayedCols();
            break;
          }
          case 'columnsOptions': {
            this.changeDisplayedCols();
            break;
          }
        }
      }
    }
  }

  changeDisplayedCols(): void {
    if (Object.keys(this.columnsOptions).length === 0) {
      this.displayedColumns = Object.keys(this.dataSource[0]);
    } else {
      this.displayedColumns = [];
      Object.entries(this.columnsOptions).forEach((v) => {
        v[1].id = v[0];
        if (v[1].display === undefined || v[1].display === true) {
          this.displayedColumns.push(v[0]);
        }
      });
    }
  }

  getRowClass(row: any): string {
    if (row?.row_type === 'cluster_summary') {
      return 'bg-accent-800 bg-opacity-40 cluster-summary-row';
    }

    if (row?.row_type === 'region_summary') {
      return 'bg-accent-800 bg-opacity-40 region-summary-row';
    }

    return 'bg-accent-800 bg-opacity-40';
  }

  getCellText(row: any, columnName: string): string {
    const value = row?.[columnName];

    if (value === null || value === undefined || value === '' || value === 'N/A') {
      return '-';
    }

    if (this.columnsOptions[columnName]?.type === 'percentage') {
      return `${value}%`;
    }

    return String(value);
  }

  isSummaryIdentifierCell(row: any, columnName: string): boolean {
    return (
      ['cluster_summary', 'region_summary'].includes(row?.row_type) && columnName === 'project_name'
    );
  }

  isArchivedStatusCell(row: any, columnName: string): boolean {
    return (
      columnName === 'archived_status' &&
      ['Archived', 'Not Archived'].includes(this.getCellText(row, columnName))
    );
  }

  getArchivedStatusClass(row: any, columnName: string): string {
    const value = this.getCellText(row, columnName);

    if (value === 'Archived') {
      return 'archived-status-pill archived-status-pill--archived';
    }

    if (value === 'Not Archived') {
      return 'archived-status-pill archived-status-pill--active';
    }

    return '';
  }

  getSummaryRowLabel(row: any): string {
    if (row?.row_type === 'cluster_summary') {
      return 'Cluster';
    }

    if (row?.row_type === 'region_summary') {
      return 'Region';
    }

    return '';
  }
}
